import { create } from "zustand";
import { ChatService, type Chat, type ChatSource } from "./chat.service";
import i18n from "@/lib/i18n";

export interface Message {
	id: string;
	role: "user" | "assistant";
	content: string;
	timestamp: Date;
	sources?: ChatSource[];
}

interface ChatStore {
	// Current chat state
	currentChatId: string | null;
	currentPodWorkspaceId: string | null;
	messages: Message[];
	connectedWorkspaceIds: string[];

	// Chat history
	chatHistory: Chat[];
	isLoadingHistory: boolean;

	// Loading states
	isInitializing: boolean;
	isSendingMessage: boolean;

	// Error states
	error: string | null;

	// Actions
	initializeChat: (
		podWorkspaceId: string,
		workspaceTitle?: string,
		forceNew?: boolean,
		initialConnectedWorkspaces?: string[],
	) => Promise<void>;
	loadAllChatHistory: () => Promise<void>;
	loadChatHistory: (podWorkspaceId: string) => Promise<void>;
	loadChat: (
		chatId: string,
		initialConnectedWorkspaces?: string[],
	) => Promise<void>;
	createNewChat: (
		podWorkspaceId: string,
		workspaceTitle?: string,
		initialConnectedWorkspaces?: string[],
	) => Promise<void>;
	sendMessage: (query: string, workspaceIds?: string[]) => Promise<void>;
	setCurrentChat: (chatId: string) => void;
	setConnectedWorkspaces: (workspaceIds: string[]) => Promise<void>;
	reset: () => void;
}

/**
 * Generate a welcome message using i18n
 * Creates a new message each time to ensure fresh timestamp
 */
const getWelcomeMessage = (): Message => ({
	id: "welcome",
	role: "assistant",
	content: i18n.t("chat.welcomeMessage"),
	timestamp: new Date(),
});

export const useChatStore = create<ChatStore>((set, get) => ({
	// Initial state
	currentChatId: null,
	currentPodWorkspaceId: null,
	messages: [getWelcomeMessage()],
	connectedWorkspaceIds: [],
	chatHistory: [],
	isLoadingHistory: false,
	isInitializing: false,
	isSendingMessage: false,
	error: null,

	// Initialize a new chat session for a workspace
	initializeChat: async (
		podWorkspaceId: string,
		workspaceTitle?: string,
		forceNew: boolean = false,
		initialConnectedWorkspaces?: string[],
	) => {
		const state = get();

		// Prevent duplicate initialization - if already initializing, skip
		if (state.isInitializing) {
			console.log("Chat initialization already in progress, skipping...");
			return;
		}

		// Determine initial connected workspaces
		const initialWorkspaces = initialConnectedWorkspaces || [podWorkspaceId];

		// If workspace changed or forcing new, reset current chat
		if (forceNew || state.currentPodWorkspaceId !== podWorkspaceId) {
			// Reset chat state when switching workspaces or forcing new
			// Set the initial workspace as the only selected workspace
			set({
				currentChatId: null,
				messages: [getWelcomeMessage()],
				connectedWorkspaceIds: initialWorkspaces, // Use passed initial workspaces
				isInitializing: true,
				error: null,
				currentPodWorkspaceId: podWorkspaceId,
			});
		} else {
			// Same workspace, not forcing new, and already has a chat - skip
			if (state.currentChatId) {
				console.log("Chat already initialized for this workspace, skipping...");
				return;
			}
			set({
				isInitializing: true,
				error: null,
				currentPodWorkspaceId: podWorkspaceId,
				// Ensure connectedWorkspaceIds includes the current workspace if not already set, or use provided
				connectedWorkspaceIds:
					state.connectedWorkspaceIds.length > 0
						? state.connectedWorkspaceIds
						: initialWorkspaces,
			});
		}

		try {
			// Load all chat history first
			await get().loadAllChatHistory();

			// If forcing new chat, always create a new one
			if (forceNew) {
				await get().createNewChat(
					podWorkspaceId,
					workspaceTitle,
					initialConnectedWorkspaces,
				);
			} else {
				const { chatHistory } = get();

				// Find the most recent chat for this workspace
				const workspaceChats = chatHistory.filter(
					(chat) => chat.podWorkspaceId === podWorkspaceId,
				);

				// If there's an existing chat for this workspace, load the most recent one
				if (workspaceChats.length > 0) {
					const mostRecentChat = workspaceChats[0];
					await get().loadChat(mostRecentChat.id, initialConnectedWorkspaces);
				} else {
					// Otherwise, create a new chat
					await get().createNewChat(
						podWorkspaceId,
						workspaceTitle,
						initialConnectedWorkspaces,
					);
				}
			}
		} catch (error) {
			console.error("Failed to initialize chat:", error);
			set({
				error:
					error instanceof Error ? error.message : "Failed to initialize chat",
				isInitializing: false,
			});
		}
	},

	// Load all chat history for the user (across all workspaces)
	loadAllChatHistory: async () => {
		set({ isLoadingHistory: true, error: null });

		try {
			const chats = await ChatService.getAllChats();
			// Sort by most recent first
			const sortedChats = chats.sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);

			set({
				chatHistory: sortedChats,
				isLoadingHistory: false,
			});
		} catch (error) {
			console.error("Failed to load all chat history:", error);
			set({
				error:
					error instanceof Error
						? error.message
						: "Failed to load chat history",
				isLoadingHistory: false,
			});
		}
	},

	// Load chat history for a workspace
	loadChatHistory: async (podWorkspaceId: string) => {
		set({ isLoadingHistory: true, error: null });

		try {
			const chats = await ChatService.getChatsForWorkspace(podWorkspaceId);
			// Sort by most recent first
			const sortedChats = chats.sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);

			set({
				chatHistory: sortedChats,
				isLoadingHistory: false,
			});
		} catch (error) {
			console.error("Failed to load chat history:", error);
			set({
				error:
					error instanceof Error
						? error.message
						: "Failed to load chat history",
				isLoadingHistory: false,
			});
		}
	},

	// Load a specific chat and its messages
	loadChat: async (chatId: string, initialConnectedWorkspaces?: string[]) => {
		set({ isInitializing: true, error: null });

		try {
			const chat = await ChatService.getChatById(chatId);

			// Transform messages to local format
			const formattedMessages: Message[] = chat.messages.map((msg) => ({
				id: msg.id,
				role: msg.role === "USER" ? "user" : "assistant",
				content: msg.content,
				timestamp: new Date(msg.createdAt),
				sources: msg.sources, // Include sources if present
			}));

			// Add welcome message if no messages exist
			const messages =
				formattedMessages.length > 0
					? formattedMessages
					: [getWelcomeMessage()];

			// Load all connected workspaces (including current workspace)
			// This ensures that when user opens "Add Workspace", all connected workspaces are pre-selected
			let connectedWorkspaceIds = chat.connectedWorkspaceIds || [
				chat.podWorkspaceId,
			];

			// If initialConnectedWorkspaces are provided, they take precedence (user intent)
			if (initialConnectedWorkspaces && initialConnectedWorkspaces.length > 0) {
				connectedWorkspaceIds = initialConnectedWorkspaces;

				// If the desired workspaces are different from what's in the chat, update backend
				const currentSet = new Set(chat.connectedWorkspaceIds || []);
				const newSet = new Set(initialConnectedWorkspaces);
				const isDifferent =
					currentSet.size !== newSet.size ||
					[...currentSet].some((id) => !newSet.has(id));

				if (isDifferent) {
					// Update backend in background
					ChatService.updateConnectedWorkspaces(
						chatId,
						initialConnectedWorkspaces,
					).catch((err) =>
						console.error("Failed to sync connected workspaces override:", err),
					);
				}
			}

			set({
				currentChatId: chatId,
				currentPodWorkspaceId: chat.podWorkspaceId,
				messages,
				connectedWorkspaceIds,
				isInitializing: false,
			});
		} catch (error) {
			console.error("Failed to load chat:", error);
			set({
				error: error instanceof Error ? error.message : "Failed to load chat",
				isInitializing: false,
			});
		}
	},

	// Create a new chat
	createNewChat: async (
		podWorkspaceId: string,
		workspaceTitle?: string,
		initialConnectedWorkspaces?: string[],
	) => {
		set({ isInitializing: true, error: null });

		try {
			const chat = await ChatService.createChat({
				title: workspaceTitle || "New Chat",
				podWorkspaceId,
			});

			const connectedWorkspaceIds = initialConnectedWorkspaces || [
				podWorkspaceId,
			];

			set({
				currentChatId: chat.id,
				currentPodWorkspaceId: podWorkspaceId,
				messages: [getWelcomeMessage()],
				connectedWorkspaceIds: connectedWorkspaceIds, // Include initial workspace or selected ones
				isInitializing: false,
			});

			// If we have initial connected workspaces that are different from just the primary one, update backend
			if (initialConnectedWorkspaces && initialConnectedWorkspaces.length > 0) {
				// Update backend in background
				ChatService.updateConnectedWorkspaces(
					chat.id,
					initialConnectedWorkspaces,
				).catch((err) =>
					console.error(
						"Failed to sync connected workspaces for new chat:",
						err,
					),
				);
			}

			// Refresh all chat history
			await get().loadAllChatHistory();
		} catch (error) {
			console.error("Failed to create new chat:", error);
			set({
				error:
					error instanceof Error ? error.message : "Failed to create new chat",
				isInitializing: false,
			});
		}
	},

	// Send a message in the current chat
	sendMessage: async (query: string, workspaceIds?: string[]) => {
		const { currentChatId, currentPodWorkspaceId, messages } = get();

		if (!currentChatId || !currentPodWorkspaceId) {
			console.error("No active chat");
			return;
		}

		// Add user message optimistically
		const userMessage: Message = {
			id: Date.now().toString(),
			role: "user",
			content: query.trim(),
			timestamp: new Date(),
		};

		set({
			messages: [...messages, userMessage],
			isSendingMessage: true,
			error: null,
		});

		try {
			// workspaceIds contains all selected workspace IDs (including primary)
			// podWorkspaceId is kept for backward compatibility and validation
			// If workspaceIds is provided, backend will use it; otherwise falls back to podWorkspaceId
			const response = await ChatService.sendMessage(currentChatId, {
				query,
				podWorkspaceId: currentPodWorkspaceId, // Used for validation only
				workspaceIds, // Contains all selected workspace IDs for RAG search
				config: {
					topK: 5,
					similarityThreshold: 0.7,
					temperature: 0.3,
				},
			});

			const assistantMessage: Message = {
				id: response.assistantMessage.id,
				role: "assistant",
				content: response.assistantMessage.content,
				timestamp: new Date(response.assistantMessage.createdAt),
				sources: response.sources,
			};

			set({
				messages: [...get().messages, assistantMessage],
				isSendingMessage: false,
			});
		} catch (error) {
			console.error("Failed to send message:", error);

			const errorMessage: Message = {
				id: (Date.now() + 1).toString(),
				role: "assistant",
				content:
					"I encountered an error processing your request. Please try again.",
				timestamp: new Date(),
			};

			set({
				messages: [...get().messages, errorMessage],
				error:
					error instanceof Error ? error.message : "Failed to send message",
				isSendingMessage: false,
			});
		}
	},

	// Set current chat (for switching between chats)
	setCurrentChat: (chatId: string) => {
		get().loadChat(chatId);
	},

	// Update connected workspaces (when user selects additional workspaces)
	setConnectedWorkspaces: async (workspaceIds: string[]) => {
		const { currentChatId } = get();

		// Update local state immediately for responsive UI
		set({ connectedWorkspaceIds: workspaceIds });

		// Persist to backend if we have an active chat
		if (currentChatId) {
			try {
				// Don't await the backend call to prevent UI lag
				ChatService.updateConnectedWorkspaces(
					currentChatId,
					workspaceIds,
				).catch((error) => {
					console.error("Failed to update connected workspaces:", error);
				});

				console.log(
					`Updated connected workspaces for chat ${currentChatId}: ${workspaceIds.join(", ")}`,
				);
			} catch (error) {
				console.error("Failed to initiate update connected workspaces:", error);
			}
		}
	},

	// Reset chat state
	reset: () => {
		set({
			currentChatId: null,
			currentPodWorkspaceId: null,
			messages: [getWelcomeMessage()],
			connectedWorkspaceIds: [], // Empty on full reset
			chatHistory: [],
			isLoadingHistory: false,
			isInitializing: false,
			isSendingMessage: false,
			error: null,
		});
	},
}));
